// Angular imports
import { Component, OnInit } from '@angular/core';

// 3rd party imports
import * as moment from 'moment-timezone';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Services imports
import { WorkflowUserMetricsService } from '../../_services/workflow-user-metrics.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';

// Model imports
import { UserNameDisplayResultDto } from '../../../_models/user-name-display-result-dto';
import { AAPUserMetricsCompanyInfo } from '../../_models/workflow-user-metrics/aap-user-metrics-company-info';
import { AAPUserMetricsParameterDto } from '../../_models/workflow-user-metrics/aap-user-metrics-parameter-dto';
import { AAPUserMetricsResultDto } from '../../_models/workflow-user-metrics/aap-user-metrics-result-dto';
import { AAPUserResultDto } from '../../_models/workflow-user-metrics/aap-user-result-dto';

// Constant imports
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';

@Component({
    selector: 'app-workflow-user-metrics',
    templateUrl: './workflow-user-metrics.component.html',
    styleUrls: ['./workflow-user-metrics.component.scss']
})
export class WorkflowUserMetricsComponent implements OnInit {
    appUserSource: AAPUserResultDto[] = [];
    metricsParam: AAPUserMetricsParameterDto;
    userMetrics: AAPUserMetricsResultDto[] = [];
    selectedUserMetricRecord: AAPUserMetricsResultDto;

    companyList: AAPUserMetricsCompanyInfo[];
    aapUserList: UserNameDisplayResultDto[];

    maxDate: Date;
    labelLocation: string;
    columnsMapping: any;

    showDetailPopup = false;
    metricDetailPopupTitle: string;
    selectedMetricType: string;

    iconRefresh = IconConstants.REFRESH;

    loadingVisible = false;
    helpLink: string;

    constructor(
        private readonly workflowUserMetricsService: WorkflowUserMetricsService,
        private readonly errorService: ServerErrorService,
        private readonly ciraHelpService: CiraHelpService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.aapWfMetrics);
    }

    ngOnInit() {
        this.setDefaultValues();
        this.getAAPUserSource();
    }

    onCompanyValueChange(): void {
        this.getAAPUsersList();
    }

    onRefreshClick(): void {
        this.getUserMetrics();
    }

    onShowUserMetricsDetail(row: any): void {
        const mapping = this.columnsMapping[row.column.dataField];
        this.selectedUserMetricRecord = row.data;
        this.selectedMetricType = mapping.dataField;
        this.metricDetailPopupTitle = `${row.data.aapUser.displayName} - Work Flows ${mapping.title}`;
        this.showDetailPopup = true;
    }

    onMetricsDetailClose(): void {
        this.showDetailPopup = false;
    }

    private setDefaultValues(): void {
        this.labelLocation = 'left';
        this.metricsParam = new AAPUserMetricsParameterDto();
        this.maxDate = moment(new Date()).toDate();
        this.metricsParam.toDate = this.maxDate;
        this.metricsParam.fromDate = this.maxDate;

        this.columnsMapping = {
            totalProcessedCount: {
                title: 'Processed',
                dataField: 'processedCount'
            },
            totalScannedStepCount: {
                title: 'Scanned',
                dataField: 'scannedStepCount'
            },
            totalVerifyStepCount: {
                title: 'Verified',
                dataField: 'verifyStepCount'
            },
            totalSupervisorStepCount: {
                title: 'Supervisor Resolved',
                dataField: 'supervisorStepCount'
            },
            totalDeletedCount: {
                title: 'Deleted',
                dataField: 'deletedCount'
            },
            totalSentToVendorEditCount: {
                title: 'Sent To Vendor Edit',
                dataField: 'sentToVendorEdit'
            },
            totalMismatchCount: {
                title: 'Mismatched',
                dataField: 'mismatchCount'
            }
        };
    }

    private getAAPUserSource(): void {
        this.loadingVisible = true;
        this.workflowUserMetricsService.getAAPUser().subscribe(
            (result: AAPUserResultDto[]) => {
                this.appUserSource = result;
                this.getUniqueCompanyList();
                this.getAAPUsersList();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getUniqueCompanyList(): void {
        this.companyList = [];
        this.appUserSource.forEach((user) => {
            if (!this.companyList.find((d) => d.companyId === user.companyId)) {
                const company: AAPUserMetricsCompanyInfo = new AAPUserMetricsCompanyInfo();
                company.companyId = user.companyId;
                company.companyName = user.companyName;
                this.companyList.push(company);
            }
        });

        if (this.companyList.length === 1) {
            this.metricsParam.companyId = this.companyList[0].companyId;
        }
    }

    private getAAPUsersList(): void {
        this.aapUserList = [];
        if (!!this.metricsParam.companyId) {
            this.aapUserList = this.appUserSource
                .filter((d) => d.companyId === this.metricsParam.companyId)
                .map((m) => m.aapUser);
        } else {
            this.aapUserList = this.appUserSource.map((d) => d.aapUser);
        }

        if (this.aapUserList.length === 1) {
            this.metricsParam.aapUserId = this.aapUserList[0].id;
        }
    }

    private getUserMetrics(): void {
        this.loadingVisible = true;
        this.workflowUserMetricsService.getUserMetrics(this.metricsParam).subscribe(
            (result: AAPUserMetricsResultDto[]) => {
                this.userMetrics = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }
}
